"use client";

import { slugifyHeadline } from "@shared/lib/content";
import { Button } from "@ui/components/button";
import { CheckIcon, LinkIcon } from "lucide-react";
import Image from "next/image";
import { useState } from "react";

/**
 * Shareable pull quote for blog posts. Pairs a branded 1200×630 quote card
 * (generated with the pull-quote-image skill) with the quote as selectable
 * text and per-network share buttons. Share links point at the post with a
 * fragment anchored to this quote, so a shared link lands the reader here.
 */
export function PullQuote({
	image,
	quote,
	attribution,
	utmCampaign,
}: {
	/** The generated quote card, e.g. /images/blog/<slug>-quote.webp */
	image: string;
	quote: string;
	attribution?: string;
	/** utm_campaign for share links; use the post slug. */
	utmCampaign: string;
}) {
	const [copied, setCopied] = useState(false);
	const anchor = `quote-${slugifyHeadline(quote).slice(0, 48)}`;

	const shareUrl = (source: string) => {
		const url = new URL(window.location.href);
		url.searchParams.set("utm_source", source);
		url.searchParams.set("utm_medium", "social");
		url.searchParams.set("utm_campaign", utmCampaign);
		url.hash = anchor;
		return url.toString();
	};

	const handleShare = (platform: string) => {
		const url = encodeURIComponent(shareUrl(platform));
		const text = encodeURIComponent(`“${quote}”`);
		const intents: Record<string, string> = {
			linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
			x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
			threads: `https://www.threads.net/intent/post?text=${text}%20${url}`,
		};
		window.open(intents[platform] ?? "#", "_blank");
	};

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(shareUrl("copy"));
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Clipboard unavailable (permissions/insecure context); nothing to do.
		}
	};

	return (
		<figure id={anchor} className="not-prose my-10 scroll-mt-24">
			<div className="overflow-hidden rounded-2xl border">
				<Image
					src={image}
					alt={`Pull quote: ${quote}`}
					width={1200}
					height={630}
					sizes="(max-width: 768px) 100vw, 42rem"
					style={{ width: "100%", height: "auto" }}
				/>
			</div>
			<figcaption className="mt-3">
				<blockquote className="border-l-2 border-primary pl-3 text-sm italic text-muted-foreground">
					“{quote}”
					{attribution && (
						<span className="not-italic"> — {attribution}</span>
					)}
				</blockquote>
				<div className="mt-3 flex flex-wrap gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => handleShare("linkedin")}
						data-testid="pullquote-share-linkedin"
					>
						<Image
							src="/images/linkedin-logo.svg"
							alt=""
							width={16}
							height={16}
							className="mr-1.5 size-4"
						/>
						Share
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => handleShare("x")}
						data-testid="pullquote-share-x"
					>
						<Image
							src="/images/twitter-x-logo.svg"
							alt=""
							width={16}
							height={16}
							className="mr-1.5 size-4"
						/>
						Post
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => handleShare("threads")}
						data-testid="pullquote-share-threads"
					>
						<Image
							src="/images/threads-logo.svg"
							alt=""
							width={16}
							height={16}
							className="mr-1.5 size-4"
						/>
						Post
					</Button>
					<Button
						variant="secondary"
						size="sm"
						onClick={handleCopy}
						data-testid="pullquote-copy-link"
					>
						{copied ? (
							<CheckIcon className="mr-1.5 size-4" />
						) : (
							<LinkIcon className="mr-1.5 size-4" />
						)}
						{copied ? "Copied" : "Copy link"}
					</Button>
				</div>
			</figcaption>
		</figure>
	);
}
